import { AppState, type AppStateData } from '../../core/AppState';
import { ParserRegistry } from '../../core/ParserRegistry';


export class StructureTree {
  private container: HTMLElement;
  private state: AppState;
  private content: HTMLElement;

  constructor(container: HTMLElement, state: AppState) {
    this.container = container;
    this.state = state;

    // Initialize static layout
    this.container.innerHTML = '';

    // Header
    const header = document.createElement('div');
    header.className = 'pane-header';
    header.textContent = 'STRUCTURE';
    this.container.appendChild(header);

    // Content Area
    this.content = document.createElement('div');
    this.content.className = 'tree-content';
    this.content.style.flex = '1';
    this.content.style.overflowY = 'auto';
    this.container.appendChild(this.content);

    this.render(this.state.current);

    // Subscribe to state changes
    this.state.subscribe((data) => {
      this.render(data);
    });
  }

  private render(data: AppStateData) {
    // Preserve scroll position
    const scrollTop = this.content.scrollTop;

    this.content.innerHTML = '';

    if (data.markers.length === 0) {
      // Optional: Show message?
      return;
    }

    data.markers.forEach((m, idx) => {
      const el = document.createElement('div');
      el.className = `tree-item ${idx === data.selectedMarkerIndex ? 'selected' : ''}`;

      // Basic styling matching PoC
      el.style.padding = '0.4rem 1rem';
      el.style.cursor = 'pointer';
      el.style.display = 'flex';
      el.style.justifyContent = 'space-between';
      el.style.fontFamily = 'var(--font-mono)';
      el.style.fontSize = '0.8rem';
      el.style.borderLeft = '3px solid transparent';

      if (idx === data.selectedMarkerIndex) {
        el.style.backgroundColor = '#2a2a2a';
        el.style.borderLeftColor = 'var(--accent)';
        el.style.color = 'var(--accent)';
      }

      const parser = ParserRegistry.getParser(this.state.current.filename);
      el.title = parser.getDescription(m.name);

      const colorClass = this.getColorClass(m.name);

      let hexCode = '';
      if (typeof m.type === 'number') {
        hexCode = `FF${m.type.toString(16).toUpperCase().padStart(2, '0')}`;
      } else {
        hexCode = String(m.type);
      }

      el.innerHTML = `
        <span><span class="marker-badge" style="${colorClass}">${m.name}</span> <span style="color:#666">${hexCode}</span></span>
        <span style="color:#555; font-size:0.75rem">${m.size.toLocaleString()}b</span>
      `;

      el.onclick = () => {
        this.state.selectMarker(idx);
      };

      el.onmouseenter = () => {
        if (idx !== data.selectedMarkerIndex) el.style.backgroundColor = '#2a2a2a';
      };
      el.onmouseleave = () => {
        if (idx !== data.selectedMarkerIndex) el.style.backgroundColor = 'transparent';
      };

      this.content.appendChild(el);
    });

    // Restore scroll position
    this.content.scrollTop = scrollTop;
  }

  private getColorClass(name: string): string {
    // JPEG
    if (name === 'SOI' || name === 'EOI') return 'color: #4caf50; font-weight: bold; margin-right: 0.5rem;';
    if (name.startsWith('APP')) return 'color: #2196f3; font-weight: bold; margin-right: 0.5rem;';
    if (name === 'DQT') return 'color: #ff9800; font-weight: bold; margin-right: 0.5rem;';
    if (name === 'SOS') return 'color: #f44336; font-weight: bold; margin-right: 0.5rem;';
    if (name === 'DHT') return 'color: #9c27b0; font-weight: bold; margin-right: 0.5rem;';
    if (name.startsWith('SOF')) return 'color: #e91e63; font-weight: bold; margin-right: 0.5rem;';

    // PNG
    if (name === 'IHDR') return 'color: #e91e63; font-weight: bold; margin-right: 0.5rem;';
    if (name === 'IDAT') return 'color: #f44336; font-weight: bold; margin-right: 0.5rem;';
    if (name === 'IEND') return 'color: #4caf50; font-weight: bold; margin-right: 0.5rem;';
    if (['PLTE', 'tRNS', 'gAMA', 'cHRM', 'sRGB', 'iCCP', 'tEXt', 'zTXt', 'iTXt', 'bKGD', 'pHYs', 'sBIT', 'tIME', 'eXIf'].includes(name)) {
      return 'color: #2196f3; font-weight: bold; margin-right: 0.5rem;';
    }
    if (name === 'PNG Signature') return 'color: #4caf50; font-weight: bold; margin-right: 0.5rem;';

    // WebP
    if (name === 'RIFF' || name === 'WEBP') return 'color: #4caf50; font-weight: bold; margin-right: 0.5rem;';
    if (name.startsWith('VP8')) return 'color: #f44336; font-weight: bold; margin-right: 0.5rem;';
    if (name === 'ALPH') return 'color: #f44336; font-weight: bold; margin-right: 0.5rem;';
    if (name === 'EXIF' || name === 'ICCP' || name === 'XMP ') return 'color: #2196f3; font-weight: bold; margin-right: 0.5rem;';

    // GIF
    if (name === 'Header' || name === 'Trailer') return 'color: #4caf50; font-weight: bold; margin-right: 0.5rem;';
    if (name === 'Logical Screen Descriptor' || name === 'Global Color Table') return 'color: #2196f3; font-weight: bold; margin-right: 0.5rem;';
    if (name === 'Image Data') return 'color: #f44336; font-weight: bold; margin-right: 0.5rem;';
    if (name.includes('Extension')) return 'color: #9c27b0; font-weight: bold; margin-right: 0.5rem;';

    return 'font-weight: bold; margin-right: 0.5rem;';
  }
}
